/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function HeatmapCalendar({ entries }) {
  const [hoveredDay, setHoveredDay] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  if (!entries.length) return null;

  // Build a map of date -> { dollars, shares, count }
  const dayMap = {};
  entries.forEach(e => {
    if (!dayMap[e.date]) dayMap[e.date] = { dollars: 0, shares: 0, count: 0 };
    dayMap[e.date].dollars += e.dollars;
    dayMap[e.date].shares  += e.shares;
    dayMap[e.date].count   += 1;
  });

  // Find date range
  const dates = Object.keys(dayMap).sort();
  const firstDate = new Date(dates[0]);
  const lastDate  = new Date(dates[dates.length - 1]);

  // Build months to display
  const months = [];
  let cur = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  const end = new Date(lastDate.getFullYear(), lastDate.getMonth(), 1);
  while (cur <= end) {
    months.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }

  const maxDollars = Math.max(...Object.values(dayMap).map(d => d.dollars));
  const fmt$ = (v) => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:2 }).format(v);
  const fmtDate = (s) => { const [y,m,d] = s.split("-"); return `${d}.${m}.${y}`; };

  const getColor = (dollars) => {
    if (!dollars) return "#111";
    const intensity = dollars / maxDollars;
    if (intensity < 0.15) return "#0e2606";
    if (intensity < 0.35) return "#1a4a0a";
    if (intensity < 0.6)  return "#5aad27";
    if (intensity < 0.85) return "#5aad27";
    return "#5aad27";
  };

  const CELL = 13, GAP = 2;
  const DAY_NAMES = ["П","В","С","Ч","П","С","Н"];
  const MONTH_NAMES = ["ЯНУ","ФЕВ","МАР","АПР","МАЙ","ЮНИ","ЮЛИ","АВГ","СЕП","ОКТ","НОВ","ДЕК"];

  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"20px 22px", marginBottom:24 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, letterSpacing:"0.3em", color:"#888" }}>HEATMAP · PURCHASES BY DAY</div>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, color:"var(--text4)" }}>low</span>
          {[0.05, 0.2, 0.4, 0.65, 1].map((v,i) => (
            <div key={i} style={{ width:10, height:10, borderRadius:2, background:getColor(v * maxDollars) }}/>
          ))}
          <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, color:"var(--text4)" }}>high</span>
        </div>
      </div>

      <div style={{ overflowX:"auto", paddingBottom:4 }}>
        <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
          {/* Day labels */}
          <div style={{ display:"flex", flexDirection:"column", gap:GAP, paddingTop:18 }}>
            {DAY_NAMES.map((d,i) => (
              <div key={i} style={{ width:10, height:CELL, display:"flex", alignItems:"center", justifyContent:"flex-end" }}>
                {i % 2 === 0 && <span style={{ fontFamily:"Montserrat,sans-serif", fontSize:7, color:"var(--text5)", lineHeight:1 }}>{d}</span>}
              </div>
            ))}
          </div>

          {/* Month columns */}
          {months.map((monthDate) => {
            const year  = monthDate.getFullYear();
            const month = monthDate.getMonth();
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const firstDow = (monthDate.getDay() + 6) % 7; // Mon=0

            // Build 7-row grid (Mon-Sun), pad with nulls
            const grid = Array(7).fill(null).map(() => []);
            for (let day = 1; day <= daysInMonth; day++) {
              const dow = (new Date(year, month, day).getDay() + 6) % 7;
              grid[dow].push(day);
            }
            // Pad first row
            const maxCols = Math.max(...grid.map(r => r.length));

            return (
              <div key={`${year}-${month}`} style={{ display:"flex", flexDirection:"column", gap:0 }}>
                <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:8, fontWeight:700, color:"var(--text5)", marginBottom:4, letterSpacing:"0.1em", textAlign:"center" }}>
                  {MONTH_NAMES[month]}
                </div>
                <div style={{ display:"flex", gap:GAP }}>
                  {Array.from({ length: maxCols }, (_, col) => (
                    <div key={col} style={{ display:"flex", flexDirection:"column", gap:GAP }}>
                      {Array.from({ length: 7 }, (_, row) => {
                        const day = grid[row][col];
                        if (day === undefined) return <div key={row} style={{ width:CELL, height:CELL }}/>;
                        const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                        const data = dayMap[dateStr];
                        const color = getColor(data?.dollars || 0);
                        return (
                          <div
                            key={row}
                            style={{ width:CELL, height:CELL, borderRadius:2, background:color, cursor: data ? "default" : "default",
                              outline: hoveredDay === dateStr ? `1px solid #5aad27` : "none",
                              transition:"outline 0.1s" }}
                            onMouseEnter={(e) => {
                              if (data) {
                                setHoveredDay(dateStr);
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipPos({ x: rect.left + rect.width/2, y: rect.top });
                              }
                            }}
                            onMouseLeave={() => setHoveredDay(null)}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fixed tooltip */}
      {hoveredDay && dayMap[hoveredDay] && (
        <div style={{ position:"fixed", left:tooltipPos.x, top:tooltipPos.y - 8, transform:"translate(-50%, -100%)",
          background:"var(--bg3)", border:"1px solid #5aad27", borderRadius:8, padding:"10px 14px",
          fontFamily:"Montserrat,sans-serif", zIndex:9999, pointerEvents:"none",
          }}>
          <div style={{ fontSize:9, color:"var(--text2)", letterSpacing:"0.2em", marginBottom:6 }}>{fmtDate(hoveredDay)}</div>
          <div style={{ fontSize:15, fontWeight:700, color:"#5aad27", marginBottom:4 }}>{fmt$(dayMap[hoveredDay].dollars)}</div>
          <div style={{ fontSize:11, color:"var(--text2)" }}>{dayMap[hoveredDay].shares.toFixed(4)} shares</div>
          {dayMap[hoveredDay].count > 1 && (
            <div style={{ fontSize:10, color:"var(--text2)", marginTop:3 }}>{dayMap[hoveredDay].count} transactions</div>
          )}
          <div style={{ width:0, height:0, borderLeft:"5px solid transparent", borderRight:"5px solid transparent", borderTop:"5px solid #5aad27", margin:"6px auto 0" }}/>
        </div>
      )}
    </div>
  );
}

export default HeatmapCalendar;
